import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaInfoCircle,
  FaLock,
  FaSignOutAlt,
  FaUserCircle,
} from "react-icons/fa";
import { FiArrowLeft } from "react-icons/fi";
import { MdOutlineDashboardCustomize } from "react-icons/md";

import { styleFor } from "./navUtils";

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const isCustomPagesSection = (label = "") =>
  normalizeText(label).includes("personnalise");

const itemHasChildren = (item) =>
  Array.isArray(item?.children) && item.children.length > 0;

const itemHasPageLink = (item) => Boolean(item?.url || item?.page?.slug);

const isCustomTabItem = (item) => {
  // Onglet / catégorie : soit il a des enfants,
  // soit il n’a pas de lien direct.
  return itemHasChildren(item) || !itemHasPageLink(item);
};

function MenuReveal({ children, delay = 0, className = "" }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 40);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ${
        visible
          ? "translate-y-0 opacity-100 blur-0"
          : "translate-y-5 opacity-0 blur-sm"
      } ${className}`}
    >
      {children}
    </div>
  );
}

function MenuSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="rounded-[2rem] border border-white/10 bg-white/5 p-5"
        >
          <div className="mb-5 h-7 w-44 animate-pulse rounded-full bg-white/10" />
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((__, itemIndex) => (
              <div
                key={itemIndex}
                className="h-12 animate-pulse rounded-2xl bg-white/10"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MenuLeafButton({ item, onClick, variant = "standard" }) {
  const isCustom = variant === "custom";

  return (
    <button
      type="button"
      onClick={onClick}
      title={item.title_attr || item.label}
      className={`group relative flex min-h-13 w-full items-center justify-between gap-3 overflow-hidden rounded-2xl px-4 py-3 text-left text-base font-bold shadow-sm transition duration-300 hover:-translate-y-0.5 md:text-[1.02rem] ${
        isCustom
          ? "border border-emerald-300/20 bg-gradient-to-r from-emerald-500/95 to-teal-500/90 text-white shadow-emerald-950/20 hover:from-emerald-400 hover:to-teal-400"
          : "border border-white/5 bg-white/[0.075] text-white hover:border-white/15 hover:bg-white/[0.12]"
      }`}
    >
      <span
        className={`pointer-events-none absolute inset-y-0 left-0 w-1 opacity-0 transition group-hover:opacity-100 ${
          isCustom
            ? "bg-white/80"
            : "bg-gradient-to-b from-sky-400 to-emerald-400"
        }`}
      />

      {isCustom && (
        <>
          <span className="pointer-events-none absolute -right-8 -top-8 h-20 w-20 rounded-full bg-white/20 blur-2xl transition group-hover:bg-white/30" />
          <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition duration-700 group-hover:translate-x-full" />
        </>
      )}

      <span className="relative leading-6">{item.label}</span>

      <span
        className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition ${
          isCustom
            ? "bg-white/18 text-white group-hover:bg-white/25"
            : "bg-white/10 text-white/55 group-hover:bg-white/15 group-hover:text-white"
        }`}
      >
        <FaArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

function MenuTabButton({ item, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={item.title_attr || item.label}
      className="group relative flex min-h-12 w-full items-center justify-between gap-3 overflow-hidden rounded-2xl border border-emerald-300/25 bg-emerald-500/15 px-4 py-3 text-left text-sm font-bold uppercase tracking-wide text-emerald-50 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-emerald-200/40 hover:bg-emerald-400/25"
    >
      <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-emerald-400/20 via-teal-300/10 to-transparent opacity-80" />
      <span className="pointer-events-none absolute -right-8 -top-8 h-20 w-20 rounded-full bg-emerald-200/15 blur-2xl transition group-hover:bg-emerald-200/25" />

      <span className="relative leading-5">{item.label}</span>

      {itemHasPageLink(item) && (
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white/70 transition group-hover:bg-white/15 group-hover:text-white">
          <FaArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
        </span>
      )}
    </button>
  );
}

function DemoApplicationCard({ onNavigate }) {
  return (
    <MenuReveal delay={90}>
      <article className="relative overflow-hidden rounded-[1.75rem] border border-sky-300/25 bg-gradient-to-r from-sky-600 via-blue-700 to-indigo-700 p-4 shadow-2xl shadow-blue-950/30">
        <div className="absolute -right-12 -top-14 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
        <div className="absolute -bottom-16 left-1/3 h-28 w-28 rounded-full bg-cyan-300/20 blur-2xl" />

        <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700 shadow-lg shadow-blue-950/20">
              <FaInfoCircle className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <h3 className="text-xl font-bold leading-tight text-white md:text-2xl">
                Informations MonAppliMairie
              </h3>

              <p className="mt-1 max-w-3xl text-xs font-semibold leading-6 text-sky-50/90 md:text-sm">
                Fonctionnalités, tarifs, démonstration et aperçu du tableau de
                bord.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onNavigate("/monapplimairie")}
            className="group inline-flex min-h-12 shrink-0 items-center justify-center gap-3 overflow-hidden rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-slate-950/20 transition duration-300 hover:-translate-y-0.5 hover:bg-slate-100 md:text-base"
          >
            <MdOutlineDashboardCustomize className="h-5 w-5 text-sky-600" />
            Découvrir
            <FaArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-1" />
          </button>
        </div>
      </article>
    </MenuReveal>
  );
}

function AccountActions({
  effectiveUser,
  permissionsLoaded,
  initials,
  brandBg,
  onNavigate,
  onAdminAccess,
  onLogout,
}) {
  if (!effectiveUser) {
    return (
      <button
        type="button"
        onClick={() => onNavigate("/login")}
        className="group inline-flex min-h-12 items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/10 px-5 py-3 text-base font-bold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-white/15"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/15">
          <FaLock className="h-4 w-4" />
        </span>
        Accès administration
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold text-white shadow-lg shadow-black/20"
        style={brandBg}
      >
        {initials}
      </div>

      <button
        type="button"
        onClick={onAdminAccess}
        disabled={!permissionsLoaded}
        className="inline-flex min-h-12 items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-5 py-3 text-base font-bold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FaUserCircle className="h-4 w-4" />
        Tableau de bord
      </button>

      <button
        type="button"
        onClick={onLogout}
        className="inline-flex min-h-12 items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-5 py-3 text-base font-bold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-white/15"
      >
        <FaSignOutAlt className="h-4 w-4" />
        Déconnexion
      </button>
    </div>
  );
}

export default function NavbarMegaMenu({
  isDemo,
  tree,
  loadingMenu,
  effectiveUser,
  permissionsLoaded,
  initials,
  brandBg,
  onClose,
  onNavigate,
  onAdminAccess,
  onLogout,
  goToItem,
}) {
  return (
    <div
      id="mega-menu"
      className="fixed left-0 right-0 top-[80px] z-30 max-h-[calc(100vh-80px)] overflow-y-auto bg-[#020617]/85 py-6 text-white shadow-2xl shadow-black/50 backdrop-blur-md md:top-[100px] md:max-h-[calc(100vh-100px)] md:py-8"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.08),transparent_34%)]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        <div className="absolute left-8 top-16 h-52 w-52 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="absolute bottom-10 right-10 h-64 w-64 rounded-full bg-emerald-400/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[1380px] px-4 sm:px-6">
        <MenuReveal delay={0}>
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => {
                onClose();
                onNavigate("/");
              }}
              className="group inline-flex min-h-12 w-fit items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-5 py-3 text-base font-bold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-white/15"
            >
              <FiArrowLeft className="h-5 w-5 transition group-hover:-translate-x-1" />
              Revenir à l’accueil
            </button>

            <AccountActions
              effectiveUser={effectiveUser}
              permissionsLoaded={permissionsLoaded}
              initials={initials}
              brandBg={brandBg}
              onNavigate={onNavigate}
              onAdminAccess={onAdminAccess}
              onLogout={onLogout}
            />
          </div>
        </MenuReveal>

        {isDemo && (
          <div className="mb-6">
            <DemoApplicationCard onNavigate={onNavigate} />
          </div>
        )}

        {loadingMenu ? (
          <MenuSkeleton />
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {tree.map((root, index) => {
              const sty = styleFor(index);
              const isCustomSection = isCustomPagesSection(root.label);

              const hasColumns =
                Array.isArray(root.children) &&
                root.children.some((child) => child.children?.length);

              return (
                <MenuReveal
                  key={root.id || root.label}
                  delay={140 + index * 90}
                >
                  <section
                    className={`group relative overflow-hidden rounded-[2rem] border p-5 shadow-2xl shadow-black/20 backdrop-blur-xl transition duration-300 hover:-translate-y-1 ${
                      isCustomSection
                        ? "border-emerald-400/70 bg-emerald-950/30 hover:bg-emerald-950/40"
                        : `${sty.border} bg-white/[0.055] hover:bg-white/[0.075]`
                    }`}
                  >
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" />

                    <div
                      className={`pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full blur-2xl transition ${
                        isCustomSection
                          ? "bg-emerald-300/15 group-hover:bg-emerald-300/20"
                          : "bg-white/5 group-hover:bg-white/10"
                      }`}
                    />

                    {isCustomSection && (
                      <div className="pointer-events-none absolute -bottom-10 left-8 h-28 w-28 rounded-full bg-teal-300/10 blur-2xl" />
                    )}

                    <div className="relative mb-5 flex items-center gap-3">
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-lg shadow-black/20 ${
                          isCustomSection
                            ? "bg-gradient-to-br from-emerald-400 to-teal-500"
                            : sty.badge
                        }`}
                        aria-hidden="true"
                      >
                        {String(root.label || "?")
                          .charAt(0)
                          .toUpperCase()}
                      </span>

                      <div>
                        <h3 className="text-xl font-bold leading-tight text-white md:text-2xl">
                          {root.label}
                        </h3>

                        <p
                          className={`mt-1 text-xs font-bold uppercase tracking-[0.18em] ${
                            isCustomSection
                              ? "text-emerald-200/70"
                              : "text-white/40"
                          }`}
                        >
                          {isCustomSection
                            ? "Pages personnalisées"
                            : "Navigation"}
                        </p>
                      </div>
                    </div>

                    {hasColumns ? (
                      <div className="relative grid gap-5 sm:grid-cols-2">
                        {root.children.map((column, columnIndex) => (
                          <div key={column.id || column.label}>
                            <div
                              className={`mb-3 rounded-2xl border px-4 py-3 text-sm font-bold uppercase tracking-wide ${
                                isCustomSection
                                  ? "border-emerald-300/20 bg-emerald-400/15 text-emerald-50"
                                  : "border-white/10 bg-white/10 text-white/90"
                              }`}
                            >
                              {column.label}
                            </div>

                            <div className="space-y-2">
                              {(column.children || []).map(
                                (leaf, leafIndex) => {
                                  const isTab =
                                    isCustomSection && isCustomTabItem(leaf);

                                  return (
                                    <MenuReveal
                                      key={leaf.id || leaf.label}
                                      delay={
                                        220 +
                                        index * 80 +
                                        columnIndex * 60 +
                                        leafIndex * 35
                                      }
                                    >
                                      {isTab ? (
                                        <MenuTabButton
                                          item={leaf}
                                          onClick={() => {
                                            if (itemHasPageLink(leaf)) {
                                              goToItem(leaf);
                                            }
                                          }}
                                        />
                                      ) : (
                                        <MenuLeafButton
                                          item={leaf}
                                          variant="standard"
                                          onClick={() => goToItem(leaf)}
                                        />
                                      )}
                                    </MenuReveal>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className={`relative grid gap-2 ${
                          isCustomSection ? "sm:grid-cols-2" : ""
                        }`}
                      >
                        {(root.children || []).map((child, childIndex) => {
                          const isTab =
                            isCustomSection && isCustomTabItem(child);

                          return (
                            <MenuReveal
                              key={child.id || child.label}
                              delay={220 + index * 80 + childIndex * 35}
                            >
                              {isTab ? (
                                <MenuTabButton
                                  item={child}
                                  onClick={() => {
                                    if (itemHasPageLink(child)) {
                                      goToItem(child);
                                    }
                                  }}
                                />
                              ) : (
                                <MenuLeafButton
                                  item={child}
                                  variant="standard"
                                  onClick={() => goToItem(child)}
                                />
                              )}
                            </MenuReveal>
                          );
                        })}
                      </div>
                    )}
                  </section>
                </MenuReveal>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
